import { paintIconAction } from './82-icon-actions.mjs';

/** The same primary button keeps its action, focus and full accessible name
 * while the arcade glyph distinguishes start, continuation and recovery. */
export function paintBobStart(ctx, button, label) {
  const icon =
    label === 'Next world'
      ? 'arrow-right'
      : ['Try again', 'Play again', 'Reload scene'].includes(label) || label.startsWith('Recover:')
        ? 'rotate-ccw'
        : 'play';
  paintIconAction(ctx, button, icon, label);
}

/** Paint final domain-owned rewards without recomputing or staging fake points. */
export function paintBobResult({ ctx, state, result, eyebrow, title, description, results, play }) {
  eyebrow.textContent = `${state.score.toLocaleString()} POINTS · WORLD ${state.level}`;
  title.textContent =
    state.status === 'complete'
      ? state.level === 20
        ? 'World tour complete!'
        : 'Picnic perfect!'
      : 'One more bounce?';
  const failure = {
    'missed-gate':
      'Missed the glowing gate. Hold an arrow or drag toward its opening; every gate is part of the course.',
    'out-of-bounds': 'Bob slipped over the edge. Release steering sooner to stay on the course.',
    overshoot:
      'Missed the finish arch. Aim toward the golden opening; keep bouncing all the way through.',
    hazard: 'Bob met a hazard. Aim around the marked blocks and watch their movement.',
    grounded:
      'Bob stumbled with no goodies. Collect food or smash crates to protect the next bounce.',
    stumbles:
      'Bob stumbled with no goodies. Collect food or smash crates to protect the next bounce.',
    'time-limit':
      'Time for a fresh run. Time your taps at landing and keep moving toward the picnic.',
  };
  description.textContent =
    state.status === 'complete'
      ? `${result.medals.join(' · ')} · ${(state.elapsed / 1000).toFixed(1)}s. ${state.assisted || state.recoveries ? 'Practice finish. Clean time not recorded.' : 'Try two bonus routes for Explorer.'}`
      : failure[state.reason] || 'Tap near landing and steer through the golden finish arch.';
  results.replaceChildren();
  results.hidden = state.status !== 'complete';
  if (!results.hidden)
    for (const [label, points] of [
      ['Collection', result.breakdown.collection],
      ['Bonus routes', result.breakdown.routes],
      ['Timing', result.breakdown.timing],
      ['Arcade chains', result.breakdown.arcade],
      ['Travel', result.breakdown.speed],
      [
        result.finishBounce
          ? `Finish bounce · ${result.finishBounce.height.toFixed(2)}m · ×${result.finishBounce.multiplier}`
          : 'Finish bounce',
        result.breakdown['finish-bounce'],
      ],
      ['Gates + finish', (result.breakdown.checkpoint || 0) + (result.breakdown.complete || 0)],
    ]) {
      results.append(ctx.e('span', '', label), ctx.e('strong', '', (points || 0).toLocaleString()));
    }
  const action =
    state.status === 'complete'
      ? state.level === 20
        ? 'Play again'
        : 'Next world'
      : state.checkpoint
        ? `Recover: ${state.recoveryName || 'checkpoint'}`
        : 'Try again';
  paintBobStart(ctx, play, action);
}

/** Nonblocking cinematic captions; only Skip is interactive, and the sequence
 * owner keeps its resource gate closed until actual preparation settles. */
export function createBobSequenceView(ctx, stage, onSkip) {
  const root = ctx.e('div', 'bob-sequence');
  const label = ctx.e('span', 'bob-sequence-label');
  const title = ctx.e('strong', 'bob-sequence-title');
  const detail = ctx.e('span', 'bob-sequence-detail');
  const skip = ctx.e('button', 'game-action bob-sequence-skip', 'Skip intro');
  skip.type = 'button';
  root.append(label, title, detail, skip);
  stage.append(root);
  skip.addEventListener('click', onSkip);
  return {
    paint(sequence, state, level) {
      const { phase, assets } = sequence;
      const fallback =
        assets && ['character', 'crate', 'backdrop'].some((key) => assets[key] === false);
      root.hidden = !sequence.active;
      root.dataset.phase = phase;
      skip.hidden = !['preparing', 'next', 'intro', 'end', 'reward'].includes(phase);
      skip.textContent = ['end', 'reward'].includes(phase)
        ? 'Show results'
        : sequence.skipped
          ? 'Intro skipped'
          : 'Skip intro';
      skip.disabled = sequence.skipped && ['next', 'preparing'].includes(phase);
      label.textContent =
        phase === 'reward' ? 'EARNED THIS RUN' : `WORLD ${level.id} · ${level.name}`;
      title.textContent =
        {
          next: 'Next adventure',
          preparing: 'Getting the world ready',
          intro: 'Meet Bob!',
          ready: 'Ready?',
          countdown: String(sequence.countdown),
          end: 'Beautiful bounce!',
          reward: `${state.score.toLocaleString()} points`,
        }[phase] || '';
      detail.textContent =
        phase === 'preparing'
          ? sequence.skipped
            ? 'The intro is skipped. Finishing the world first…'
            : 'Character · scenery · course'
          : fallback && ['intro', 'ready', 'countdown'].includes(phase)
            ? 'Classic artwork ready · you can still play this world'
            : phase === 'intro'
              ? 'One little Bob. Twenty big adventures.'
              : phase === 'reward'
                ? `Best chain ${state.arcade?.bestChain || 0} · every point earned in play`
                : phase === 'countdown'
                  ? 'Get ready to Bounce'
                  : '';
    },
    dispose() {
      skip.removeEventListener('click', onSkip);
      root.remove();
    },
  };
}
